import { LegacyRouteHandler } from "@/components/auth/legacy-route-handler";
import { BillingRedirect } from "@/components/auth/billing-redirect";
import { ClerkProtectedRoute } from "@/components/auth/clerk-protected-route";
import { WorkspaceRouteWrapper } from "@/components/auth/workspace-route-wrapper";
import { BulkAddDealsPage } from "@/components/deals/bulk-add-deals";
import { DealsLayout } from "@/components/layouts/deals-layout";
import { MinimalLayout } from "@/components/layouts/minimal-layout";
import { SidebarLayout } from "@/components/layouts/sidebar-layout";
import NotFound from "@/components/pages/not-found";
import Archive from "@/pages/archive";
import { CreateSandboxPage } from "@/pages/create-sandbox";
import { Dashboard } from "@/pages/dashboard";
import DealRadar from "@/pages/deal-radar";
import Deals from "@/pages/deals";
import GettingStarted from "@/pages/getting-started";
import Portal from "@/pages/portal/portal-page";
import SubmissionPage from "@/pages/portal/submission-page";
import Portco from "@/pages/portco";
import RaiseBoardPage from "@/pages/raise-board/raise-board";
import RaiseBoardPublic from "@/pages/raise-board/raise-board-public";
import TeamReviews from "@/pages/reviews";
import Team from "@/pages/team";
import Inbox from "@/pages/inbox";
import InboxDetail from "@/pages/inbox-detail";
import ListWorkspaces from "@/pages/workspaces/list-workspaces";
import { Navigate, Route, Routes } from "react-router";
import { AuthPage } from "../pages/auth";
import { InvitePage } from "../pages/invite";
import { BookDemoPage } from "../pages/book-demo";
import { SSOCallbackPage } from "@/pages/sso-callback";
import DealDetailPage from "@/pages/deal-detail/deal-detail-page";
import { SettingsLayout } from "@/components/layouts/settings-layout";
import { OrgSettingsLayout } from "@/components/layouts/org-settings-layout";
import { SettingsIndexPage } from "@/components/settings/settings-index-page";
import GeneralSettings from "@/components/settings/general-settings";
import ApplicationsSettings from "@/components/settings/applications-settings";
import RaiseBoardSettings from "@/components/settings/raise-board-settings";
import AIDealQuestionsSettings from "@/components/settings/ai-deal-questions-settings";
import PortcoUpdatesSettings from "@/components/settings/portco-updates-settings";
import ThesisSettings from "@/components/settings/thesis-settings";
import MembersSettings from "@/components/settings/members-settings";
import OrgBilling from "@/components/settings/org-billing";
import WorkflowSettings from "@/components/settings/workflow-settings";
import InboxSettings from "@/components/settings/inbox-settings";

export const routes = (
  <Routes>
    {/* Public routes */}
    <Route path="/" element={<AuthPage />} />
    <Route path="/sign-in" element={<AuthPage />} />
    <Route path="/book-demo" element={<BookDemoPage />} />
    <Route path="/forgot-password" element={<AuthPage />} />
    <Route path="/reset-password" element={<AuthPage />} />
    <Route path="/invite" element={<InvitePage />} />
    <Route path="/get-started" element={<CreateSandboxPage />} />
    <Route path="/start-trial" element={<Navigate to="/get-started" replace />} />
    <Route path="/portal" element={<Portal />} />
    <Route path="/p/:slug" element={<Portal />} />
    <Route path="/p/:slug/submission" element={<SubmissionPage />} />
    <Route path="/rb/:slug" element={<RaiseBoardPublic />} />
    <Route path="/portal/submission" element={<SubmissionPage />} />
    <Route path="/sso-callback" element={<SSOCallbackPage />} />

    {/* Workspace-less route for listing all workspaces */}
    <Route element={<MinimalLayout isLoading={false} />}>
      <Route
        path="/workspaces"
        element={
          <ClerkProtectedRoute>
            <ListWorkspaces />
          </ClerkProtectedRoute>
        }
      />
    </Route>

    {/* Org settings — company-scoped billing */}
    <Route
      path="/org/:orgId/settings"
      element={
        <ClerkProtectedRoute>
          <OrgSettingsLayout />
        </ClerkProtectedRoute>
      }
    >
      <Route index element={<Navigate to="billing" replace />} />
      <Route path="billing" element={<OrgBilling />} />
    </Route>

    {/* Workspace-prefixed routes */}

    <Route
      path="/:workspaceId"
      element={
        <ClerkProtectedRoute>
          <WorkspaceRouteWrapper />
        </ClerkProtectedRoute>
      }
    >
      {/* Settings routes — standalone layout (no main sidebar) */}
      <Route path="settings" element={<SettingsLayout />}>
        <Route index element={<SettingsIndexPage />} />
        <Route path="general" element={<GeneralSettings />} />
        <Route path="public-identity" element={<Navigate to="../general" replace />} />
        <Route path="applications" element={<ApplicationsSettings />} />
        <Route path="portco-updates" element={<PortcoUpdatesSettings />} />
        <Route path="raise-board" element={<RaiseBoardSettings />} />
        <Route path="ai-deal-questions" element={<AIDealQuestionsSettings />} />
        <Route path="application-form" element={<Navigate to="../ai-deal-questions" replace />} />
        <Route path="submission-form" element={<Navigate to="../applications" replace />} />
        <Route path="landing-page" element={<Navigate to="../general" replace />} />
        <Route path="thesis" element={<ThesisSettings />} />
        <Route path="members" element={<MembersSettings />} />
        <Route path="billing" element={<BillingRedirect />} />
        <Route path="inbox" element={<InboxSettings />} />
        <Route path="workflow" element={<WorkflowSettings />} />
      </Route>

      {/* Backward compat redirects */}
      <Route path="workspace-settings" element={<Navigate to="settings" replace />} />
      <Route path="workspace-settings/deal-fit" element={<Navigate to="settings/thesis" replace />} />
      <Route path="workspace-settings/team" element={<Navigate to="settings/members" replace />} />
      <Route path="workspace-settings/portal" element={<Navigate to="settings/general" replace />} />
      <Route path="workspace-settings/questionnaire" element={<Navigate to="settings/ai-deal-questions" replace />} />
      <Route path="workspace-settings/portal-form" element={<Navigate to="settings/applications" replace />} />
      <Route path="workspace-settings/workflow" element={<Navigate to="settings/workflow" replace />} />
      <Route path="workspace-settings/billing" element={<Navigate to="settings/billing" replace />} />

      {/* Main app routes with sidebar layout */}
      <Route element={<SidebarLayout />}>
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="inbox" element={<Inbox />} />
        <Route path="inbox/:emailId" element={<InboxDetail />} />
        <Route path="deals" element={<Deals />} />
        <Route path="deals/bulk-add" element={<BulkAddDealsPage />} />
        <Route path="archive" element={<Archive />} />
        <Route path="portco" element={<Portco />} />
        <Route path="raise-board" element={<RaiseBoardPage />} />
        <Route path="reviews" element={<TeamReviews />} />
        <Route path="team" element={<Team />} />
        <Route path="getting-started" element={<GettingStarted />} />
        <Route path="deal-radar" element={<DealRadar />} />
      </Route>

      {/* Deal detail routes with deals layout */}
      <Route element={<DealsLayout />}>
        {/* New tabbed deal detail page */}
        <Route path="deals/:id" element={<DealDetailPage />} />
      </Route>
    </Route>

    {/* Legacy routes - redirect to workspace-prefixed routes */}
    <Route
      path="/dashboard"
      element={
        <ClerkProtectedRoute>
          <LegacyRouteHandler />
        </ClerkProtectedRoute>
      }
    />
    <Route
      path="/deals"
      element={
        <ClerkProtectedRoute>
          <LegacyRouteHandler />
        </ClerkProtectedRoute>
      }
    />
    <Route
      path="/getting-started"
      element={
        <ClerkProtectedRoute>
          <LegacyRouteHandler />
        </ClerkProtectedRoute>
      }
    />
    <Route
      path="/deals/:id/*"
      element={
        <ClerkProtectedRoute>
          <LegacyRouteHandler />
        </ClerkProtectedRoute>
      }
    />
    <Route path="*" element={<NotFound />} />
  </Routes>
);
